import React, { useState } from 'react';
import DateInput from '../DateInput/DateInput';
import { toInputDateTime } from '../../utils/dateFormat';

// Local-time YYYY-MM-DD — never toISOString() directly, which is UTC and
// would shift the date for anyone west of Greenwich (e.g. it can slide
// "today" back to yesterday for a good chunk of the day in India).
export function toLocalISO(date) {
    const tzOffsetMs = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - tzOffsetMs).toISOString().slice(0, 10);
}

const PRESETS = [
    { label: 'Last 7 days', days: 7 },
    { label: 'Last 30 days', days: 30 },
    { label: 'Last 90 days', days: 90 },
    { label: 'Month to date', type: 'mtd' },
    { label: 'Year to date', type: 'ytd' },
];

function presetRange(preset, withTime) {
    const today = new Date();
    let start;
    if (preset.type === 'mtd') start = new Date(today.getFullYear(), today.getMonth(), 1);
    else if (preset.type === 'ytd') start = new Date(today.getFullYear(), 0, 1);
    else { start = new Date(today); start.setDate(start.getDate() - preset.days + 1); }
    if (withTime) {
        start.setHours(0, 0, 0, 0);
        return { start: toInputDateTime(start), end: toInputDateTime(today) };
    }
    return { start: toLocalISO(start), end: toLocalISO(today) };
}

/**
 * The "Select date range" card used across the reporting pages: presets,
 * Start/End date inputs, Apply/Clear. A preset applies immediately; typed
 * dates only take effect on Apply, and Apply is disabled when start > end.
 * withTime: the time of day too ('YYYY-MM-DDTHH:mm', this browser's time — send
 * it on as ISO: DateTimeRange's rangeParams).
 */
export default function DateRangeCard({ value, onApply, onClear, withTime = false }) {
    const inputType = withTime ? 'datetime-local' : 'date';
    const [start, setStart] = useState(value?.start || '');
    const [end, setEnd] = useState(value?.end || '');
    const invalid = !!(start && end && start > end);

    function applyRange(range) {
        setStart(range.start);
        setEnd(range.end);
        onApply?.(range);
    }

    function handleApply() {
        if (invalid) return;
        onApply?.({ start, end });
    }

    function handleClear() {
        setStart('');
        setEnd('');
        onClear?.();
    }

    return (
        <div className="date-range-card">
            <h3 className="date-range-title">{withTime ? 'Select date & time' : 'Select date range'}</h3>
            <div className="date-range-presets">
                {PRESETS.map((p) => (
                    <button key={p.label} type="button" className="btn-secondary-modern btn-sm" onClick={() => applyRange(presetRange(p, withTime))}>
                        {p.label}
                    </button>
                ))}
            </div>
            <div className="date-range-fields">
                <label>
                    <span>Start</span>
                    <DateInput type={inputType} value={start} onChange={(e) => setStart(e.target.value)} />
                </label>
                <label>
                    <span>End</span>
                    <DateInput type={inputType} value={end} onChange={(e) => setEnd(e.target.value)} />
                </label>
            </div>
            {invalid && <p className="date-range-error">Start must be before end</p>}
            <div className="date-range-actions">
                <button type="button" className="btn-primary-modern btn-sm" onClick={handleApply} disabled={invalid}>Apply</button>
                <button type="button" className="btn-ghost" onClick={handleClear}>Clear</button>
            </div>
        </div>
    );
}
